import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock, Fragment as _Fragment, renderList as _renderList, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "device-details-page" }
const _hoisted_2 = { class: "card-header" }
const _hoisted_3 = { style: {"display":"flex","gap":"12px","align-items":"center"} }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "package-info" }
const _hoisted_7 = { class: "package-name" }
const _hoisted_8 = {
  key: 1,
  class: "package-name"
}
const _hoisted_9 = { class: "dialog-footer" }

import { ref, onMounted, computed, reactive, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/services/apiClient";
import config from "@/config";
import { ElMessage, ElMessageBox, FormInstance, FormRules } from "element-plus";
import { logger } from "@/utils/logger";
import { formatDateTime } from "@/utils/dateTimeUtils";
import { useStore } from "vuex";
import { DataService } from "@/services/dataService";


export default /*@__PURE__*/_defineComponent({
  __name: 'DeviceDetails',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const store = useStore();
const loading = ref(false);
const device = ref<Record<string, any> | null>(null);
const device_id = ref(route.params.id as string);
// 本月剩余通话时间
const remainingLoading = ref(false);
const remainingInfo = ref<any | null>(null);

const fetchRemainingMinutes = async () => {
  if (!device_id.value) return;
  remainingLoading.value = true;
  try {
    const resp = await DataService.getDeviceMonthlyRemaining(device_id.value);
    const payload = (resp as any)?.data;
    if (payload?.code === 0) {
      remainingInfo.value = payload.data || null;
    } else {
      remainingInfo.value = null;
    }
  } catch (e) {
    remainingInfo.value = null;
  } finally {
    remainingLoading.value = false;
  }
};

const gotoThisMonthCallRecords = () => {
  if (!device_id.value) return;
  // 跳转到设备通话明细页，CallRecords 将默认筛选为本月
  router.push({ name: "设备通话明细", params: { device_id: device_id.value } });
};

// 套餐相关
const packages = ref<any[]>([]);
const packagesLoading = ref(false);
const devicePackage = ref<any>(null);

const hasPermission = (perm: string) => store.getters.hasPermission(perm);
const isSuperAdmin = computed(() => store.state.user?.role === "super_admin");

// 是否离线（用于禁用敏感操作）
const isOffline = computed(
  () => String(device.value?.status || "").toLowerCase() !== "online"
);

// 网络类型可读化
const readableNetType = computed(() => {
  const v = String(device.value?.network_type || "").toLowerCase();
  if (v === "wifi") return "WiFi";
  if (["4g", "lte", "mobile", "cellular"].includes(v)) return "4G";
  return "N/A";
});

// 强制4G开关状态
const force4g = ref(false);

// 编辑弹窗状态
const dialogVisible = ref(false);
const dialogTitle = ref("编辑设备");
const saving = ref(false);
const deviceFormRef = ref<FormInstance>();
const deviceForm = reactive({
  id: null,
  device_id: "",
  device_name: "",
  device_model: "",
  hardware_version: "",
  firmware_version: "",
  status: "offline",
  license_status: "inactive",
  license_key: "",
  package_id: 0,
});
const deviceFormRules = reactive<FormRules>({
  device_id: [{ required: true, message: "设备编号不能为空", trigger: "blur" }],
  device_name: [
    { required: true, message: "设备名称不能为空", trigger: "blur" },
  ],
});

const fetchDeviceDetails = async () => {
  if (!device_id.value) {
    console.error("DeviceDetails fetchDeviceDetails - 未提供设备ID");
    ElMessage.error("未提供设备ID");
    return;
  }
  loading.value = true;
  try {
    console.log(
      "DeviceDetails fetchDeviceDetails - 开始获取设备详情，设备ID:",
      device_id.value
    );
    // 注意：需要确保config中有一个可以构建带ID的设备URL的函数
    const url = `${config.apiUrls.DEVICES}/${device_id.value}`;
    console.log("DeviceDetails fetchDeviceDetails - 请求URL:", url);
    const response = await apiClient.get(url);
    console.log("DeviceDetails fetchDeviceDetails - 响应数据:", response.data);
    if (response.data.code === 0) {
      device.value = response.data.data;
      console.log("DeviceDetails fetchDeviceDetails - 设备详情获取成功");
      await fetchQrSourceData(device.value);
      // 初始化网络模式开关（使用网络模式偏好而非当前网络类型）
      const networkModePreference = String(
        device.value?.network_mode_preference || "auto"
      ).toLowerCase();
      force4g.value = networkModePreference === "force_4g";

      // 获取设备套餐信息（仅超级管理员可见，避免非超级管理员触发权限接口）
      if (isSuperAdmin.value) {
        await fetchDevicePackage();
      }
    } else {
      console.error(
        "DeviceDetails fetchDeviceDetails - API返回错误:",
        response.data.message
      );
      ElMessage.error(response.data.message || "获取设备详情失败");
    }
  } catch (error) {
    console.error(
      "DeviceDetails fetchDeviceDetails - 获取设备详情时发生错误:",
      error
    );
    logger.error("获取设备详情时发生错误:", error);
    ElMessage.error("网络错误，无法加载设备详情");
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.push("/deviceManagement");
};

const getStatusType = (
  status: string
): "success" | "warning" | "info" | "danger" => {
  const statusMap: Record<string, "success" | "warning" | "info" | "danger"> = {
    online: "success",
    offline: "warning",
  };
  return statusMap[status] || "info";
};

const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    online: "在线",
    offline: "离线",
  };
  return textMap[status] || "未知";
};

onMounted(() => {
  // 清理可能存在的消息通知，避免从其他页面跳转时显示异常消息
  const messageContainer = document.querySelector(".el-message");
  if (messageContainer) {
    messageContainer.remove();
  }

  // 添加调试信息
  console.log("DeviceDetails onMounted - 开始加载设备详情");

  fetchDeviceDetails();
  if (isSuperAdmin.value) {
    console.log("DeviceDetails onMounted - 开始加载套餐列表");
    fetchPackages();
  }
  // 加载本月剩余通话分钟
  fetchRemainingMinutes();
});

// 套餐相关方法
const fetchPackages = async () => {
  try {
    packagesLoading.value = true;
    console.log("DeviceDetails fetchPackages - 开始获取套餐列表");
    const response = await apiClient.get(`${config.apiBaseUrl}/packages`);
    console.log("DeviceDetails fetchPackages - 响应数据:", response.data);
    if (response.data.code === 0) {
      packages.value = response.data.data.items || [];
      console.log(
        "DeviceDetails fetchPackages - 套餐列表获取成功，数量:",
        packages.value.length
      );
    } else {
      console.error(
        "DeviceDetails fetchPackages - API返回错误:",
        response.data.message
      );
      ElMessage.error(response.data.message || "获取套餐列表失败");
    }
  } catch (error) {
    console.error("DeviceDetails fetchPackages - 获取套餐列表失败:", error);
    ElMessage.error("获取套餐列表失败");
  } finally {
    packagesLoading.value = false;
  }
};

// 获取设备套餐信息
const fetchDevicePackage = async () => {
  if (!device.value?.package_id) {
    devicePackage.value = null;
    return;
  }

  try {
    console.log(
      "DeviceDetails fetchDevicePackage - 开始获取设备套餐信息，package_id:",
      device.value.package_id
    );
    const response = await apiClient.get(
      `${config.apiBaseUrl}/packages/${device.value.package_id}`
    );
    console.log("DeviceDetails fetchDevicePackage - 响应数据:", response.data);
    if (response.data.code === 0) {
      devicePackage.value = response.data.data;
      console.log("DeviceDetails fetchDevicePackage - 设备套餐信息获取成功");
    } else {
      console.error(
        "DeviceDetails fetchDevicePackage - API返回错误:",
        response.data.message
      );
      devicePackage.value = null;
    }
  } catch (error) {
    console.error(
      "DeviceDetails fetchDevicePackage - 获取设备套餐信息失败:",
      error
    );
    devicePackage.value = null;
  }
};

const refreshPackages = () => {
  fetchPackages();
};

// 详情页操作
const onClearWifi = async () => {
  if (!device.value) return;
  if (isOffline.value) {
    ElMessage.warning("设备离线，禁止清除WiFi");
    return;
  }
  try {
    await ElMessageBox.confirm(
      `将要清除设备保存的wifi配置 ${device.value.device_name}. 继续?`,
      "确认",
      {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        type: "warning",
      }
    );
    const resp = await DataService.clearDeviceWifi(device.value.device_id);
    if ((resp as any).data?.code === 0) {
      ElMessage.success("WiFi配置清除成功");
    } else {
      ElMessage.error((resp as any).data?.message || "操作失败");
    }
  } catch {
    ElMessage.info("操作取消");
  }
};

const onForceRestart = async () => {
  if (!device.value) return;
  if (isOffline.value) {
    ElMessage.warning("设备离线，禁止重启操作");
    return;
  }
  try {
    await ElMessageBox.confirm(
      `将要强制重启: ${device.value.device_name}. 继续?`,

      "确认",
      {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        type: "warning",
      }
    );
    const resp = await DataService.forceDeviceRestart(device.value.device_id);
    if ((resp as any).data?.code === 0) {
      ElMessage.success("已下发重启指令");
    } else {
      ElMessage.error((resp as any).data?.message || "操作失败");
    }
  } catch {
    ElMessage.info("已取消操作");
  }
};

const onToggleNetworkMode = async () => {
  if (!device.value) return;
  if (isOffline.value) {
    // 由于开关被禁用，通常不会触发，但仍然做一次保护
    ElMessage.warning("设备离线，禁止设置网络模式");
    return;
  }
  const mode = force4g.value ? "force_4g" : "auto";
  const resp = await DataService.setDeviceNetworkMode(
    device.value.device_id,
    mode
  );
  if ((resp as any).data?.code === 0) {
    ElMessage.success("网络模式已更新");
  } else {
    ElMessage.error((resp as any).data?.message || "操作失败");
  }
};

// 编辑设备
const onEdit = () => {
  if (!device.value) return;
  dialogTitle.value = "编辑设备";
  // 使用 nextTick 确保表单在数据赋值前已存在
  nextTick(() => {
    Object.assign(deviceForm, device.value);
  });
  dialogVisible.value = true;
};

// 关闭弹窗并重置表单
const closeDialog = () => {
  dialogVisible.value = false;
  deviceFormRef.value?.resetFields();
  // 重置状态
  Object.assign(deviceForm, {
    id: null,
    device_id: "",
    device_name: "",
    device_model: "",
    hardware_version: "",
    firmware_version: "",
    status: "offline",
    license_status: "inactive",
    license_key: "",
    package_id: 0,
  });
};

// 保存设备
const handleSave = async () => {
  if (!deviceFormRef.value) return;
  await deviceFormRef.value.validate(async (valid) => {
    if (valid) {
      saving.value = true;
      try {
        // 编辑模式 - PUT 请求
        const url = `${config.apiUrls.DEVICES}/${deviceForm.device_id}`;
        const response = await apiClient.put(url, deviceForm);

        if (response.data.code === 0) {
          ElMessage.success("设备更新成功");
          closeDialog();
          // 重新获取设备详情以刷新页面数据
          await fetchDeviceDetails();

          // 如果套餐有变化，需要单独更新套餐（仅超级管理员允许修改）
          if (
            isSuperAdmin.value &&
            deviceForm.package_id !== device.value?.package_id
          ) {
            try {
              const actualPackageId =
                deviceForm.package_id === 0 ? null : deviceForm.package_id;
              await apiClient.put(
                `${config.apiBaseUrl}/packages/device/${deviceForm.device_id}/package`,
                { package_id: actualPackageId }
              );
              // 再次刷新设备详情以获取最新的套餐信息
              await fetchDeviceDetails();
            } catch (error) {
              console.error("更新设备套餐失败:", error);
              ElMessage.warning("设备信息更新成功，但套餐更新失败");
            }
          }
        } else {
          ElMessage.error(response.data.message || "操作失败");
        }
      } catch (error: unknown) {
        logger.error("保存设备时出错:", error);
        const errMsg =
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message || "网络错误，操作失败";
        ElMessage.error(errMsg);
      } finally {
        saving.value = false;
      }
    }
  });
};

const onDelete = async () => {
  if (!device.value) return;
  try {
    await ElMessageBox.confirm(
      `确定要删除设备 ${device.value.device_name} (${device.value.device_id}) 吗? 这将是一个不可恢复的操作。`,
      "警告",
      {
        confirmButtonText: "确定删除",
        cancelButtonText: "取消",
        type: "warning",
      }
    );
    const url = `${config.apiUrls.DEVICES}/${device.value.device_id}`;
    const response = await apiClient.delete(url);
    if (response.data.code === 0) {
      ElMessage.success("设备删除成功");
      router.push("/deviceManagement");
    } else {
      ElMessage.error(response.data.message || "删除失败");
    }
  } catch {
    ElMessage.info("已取消删除");
  }
};

const qrCodeEl = ref<HTMLElement | null>(null);
const qrCodeInstance = ref<any>(null);
const qrPayload = ref<any | null>(null);

const renderQr = async () => {
  if (!qrCodeEl.value || !qrPayload.value) return;
  const anyWin = window as any;
  if (!anyWin.QRCode) {
    console.error("QRCode 脚本未加载");
    ElMessage.error("二维码脚本未加载");
    return;
  }
  qrCodeEl.value.innerHTML = "";
  qrCodeInstance.value = new anyWin.QRCode(qrCodeEl.value, {
    text: JSON.stringify(qrPayload.value),
    width: 120,
    height: 120,
  });
};

const fetchQrSourceData = async (d: any) => {
  try {
    qrPayload.value = {
      type: 1,
      productId: 147,
      deviceNumber: d.device_id,
      SN: d.serial_number,
      productName: d.device_name || "",
    };
    await nextTick();
    await renderQr();
  } catch (e) {
    console.error("生成二维码失败:", e);
    ElMessage.error("生成二维码失败");
  }
};

return (_ctx: any,_cache: any) => {
  const _component_el_page_header = _resolveComponent("el-page-header")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_space = _resolveComponent("el-space")!
  const _component_el_descriptions_item = _resolveComponent("el-descriptions-item")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_descriptions = _resolveComponent("el-descriptions")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_page_header, {
      onBack: goBack,
      class: "page-header"
    }, {
      content: _withCtx(() => [...(_cache[12] || (_cache[12] = [
        _createElementVNode("span", { class: "text-large font-600 mr-3" }, " 设备详情 ", -1)
      ]))]),
      _: 1
    }),
    (device.value)
      ? (_openBlock(), _createBlock(_component_el_card, {
          key: 0,
          class: "box-card"
        }, {
          header: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("span", null, _toDisplayString(device.value.device_name) + " (" + _toDisplayString(device.value.device_id) + ")", 1),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_el_tag, {
                  type: getStatusType(device.value.status)
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(getStatusText(device.value.status)), 1)
                  ]),
                  _: 1
                }, 8, ["type"]),
                _createVNode(_component_el_space, null, {
                  default: _withCtx(() => [
                    (hasPermission('device.edit'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 0,
                          type: "primary",
                          size: "small",
                          onClick: onEdit
                        }, {
                          default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                            _createTextVNode("编辑", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (hasPermission('device.delete'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 1,
                          type: "danger",
                          size: "small",
                          onClick: onDelete
                        }, {
                          default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                            _createTextVNode("删除", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (hasPermission('device.manage'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 2,
                          type: "info",
                          size: "small",
                          onClick: onClearWifi,
                          disabled: isOffline.value
                        }, {
                          default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                            _createTextVNode("清除WiFi", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled"]))
                      : _createCommentVNode("", true),
                    (hasPermission('device.manage'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 3,
                          type: "danger",
                          size: "small",
                          onClick: onForceRestart,
                          disabled: isOffline.value
                        }, {
                          default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                            _createTextVNode("强制重启", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ])
            ])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_descriptions, {
              column: 2,
              border: ""
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_descriptions_item, { label: "生产序列号" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.serial_number || "N/A"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "硬件版本" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.hardware_version || "N/A"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "固件版本" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.firmware_version || "N/A"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "最后在线时间" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.last_seen_at ? _unref(formatDateTime)(device.value.last_seen_at) : "从未"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "注册时间" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(formatDateTime)(device.value.created_at)), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "License过期时间" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.license_expires_at
            ? _unref(formatDateTime)(device.value.license_expires_at)
            : "N/A"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "激活IP地址" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.registration_ip || "N/A"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "当前上网类型" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(readableNetType.value), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "IMEI" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.imei || "N/A"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "IMSI" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(device.value.imsi || "N/A"), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "本月剩余通话时间" }, {
                  default: _withCtx(() => [
                    (remainingLoading.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_4, "加载中..."))
                      : (_openBlock(), _createElementBlock("span", _hoisted_5, [
                          (remainingInfo.value && remainingInfo.value.unlimited)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(" 不限额 ")
                              ], 64))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode(_toDisplayString(Math.max(0, remainingInfo.value?.remaining_minutes || 0)) + " 分钟 ", 1)
                              ], 64))
                        ]))
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "本月通话记录" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      onClick: gotoThisMonthCallRecords
                    }, {
                      default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                        _createTextVNode(" 查看记录 ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                (isSuperAdmin.value)
                  ? (_openBlock(), _createBlock(_component_el_descriptions_item, {
                      key: 0,
                      label: "License密钥",
                      span: 2
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          modelValue: device.value.license_key,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((device.value.license_key) = $event)),
                          type: "textarea",
                          rows: 3,
                          readonly: "",
                          placeholder: "暂无License密钥"
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (isSuperAdmin.value)
                  ? (_openBlock(), _createBlock(_component_el_descriptions_item, {
                      key: 1,
                      label: "套餐信息",
                      span: 2
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_6, [
                          (devicePackage.value)
                            ? (_openBlock(), _createBlock(_component_el_tooltip, {
                                key: 0,
                                content: `${devicePackage.value.description || '暂无描述'}`,
                                placement: "top",
                                effect: "dark"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_7, _toDisplayString(devicePackage.value.name), 1)
                                ]),
                                _: 1
                              }, 8, ["content"]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_8, "无套餐"))
                        ])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_el_descriptions_item, {
                  label: "网络模式偏好",
                  span: 2
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_switch, {
                      modelValue: force4g.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((force4g).value = $event)),
                      "active-text": 'Force 4G',
                      "inactive-text": 'Auto',
                      onChange: onToggleNetworkMode,
                      disabled: isOffline.value
                    }, null, 8, ["modelValue", "disabled"]),
                    _cache[18] || (_cache[18] = _createElementVNode("div", { class: "network-mode-tip" }, "切换模式会立即重启设备", -1))
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_descriptions_item, { label: "二维码" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", {
                      ref_key: "qrCodeEl",
                      ref: qrCodeEl,
                      class: "qr-box"
                    }, null, 512)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : (!loading.value)
        ? (_openBlock(), _createBlock(_component_el_empty, {
            key: 1,
            description: "无法加载设备数据"
          }))
        : _createCommentVNode("", true),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((dialogVisible).value = $event)),
      title: dialogTitle.value,
      width: "600px",
      "close-on-click-modal": false
    }, {
      footer: _withCtx(() => [
        _createElementVNode("span", _hoisted_9, [
          _createVNode(_component_el_button, { onClick: closeDialog }, {
            default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
              _createTextVNode("取消", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: handleSave,
            loading: saving.value
          }, {
            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
              _createTextVNode("保存", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          ref_key: "deviceFormRef",
          ref: deviceFormRef,
          model: deviceForm,
          rules: deviceFormRules,
          "label-width": "120px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "设备编号",
              prop: "device_id"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.device_id,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((deviceForm.device_id) = $event)),
                  placeholder: "请输入设备编号",
                  disabled: true
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "设备名称",
              prop: "device_name"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.device_name,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((deviceForm.device_name) = $event)),
                  placeholder: "请输入设备名称"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "设备型号",
              prop: "device_model"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.device_model,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((deviceForm.device_model) = $event)),
                  placeholder: "请输入设备型号"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "硬件版本",
              prop: "hardware_version"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.hardware_version,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((deviceForm.hardware_version) = $event)),
                  placeholder: "请输入硬件版本"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "固件版本",
              prop: "firmware_version"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.firmware_version,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((deviceForm.firmware_version) = $event)),
                  placeholder: "请输入固件版本"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "设备状态",
              prop: "status"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_select, {
                  modelValue: deviceForm.status,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((deviceForm.status) = $event)),
                  placeholder: "设备状态"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_option, {
                      label: "在线",
                      value: "online"
                    }),
                    _createVNode(_component_el_option, {
                      label: "离线",
                      value: "offline"
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            (isSuperAdmin.value)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 0,
                  label: "License状态",
                  prop: "license_status"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: deviceForm.license_status,
                      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((deviceForm.license_status) = $event)),
                      placeholder: "License状态"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "已激活",
                          value: "active"
                        }),
                        _createVNode(_component_el_option, {
                          label: "未激活",
                          value: "inactive"
                        }),
                        _createVNode(_component_el_option, {
                          label: "已过期",
                          value: "expired"
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isSuperAdmin.value)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 1,
                  label: "License密钥",
                  prop: "license_key"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: deviceForm.license_key,
                      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((deviceForm.license_key) = $event)),
                      type: "textarea",
                      rows: 3,
                      placeholder: "请输入License密钥"
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isSuperAdmin.value)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 2,
                  label: "套餐",
                  prop: "package_id"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: deviceForm.package_id,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((deviceForm.package_id) = $event)),
                      placeholder: "选择套餐",
                      style: {"width":"100%"}
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "无套餐",
                          value: 0
                        }),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(packages.value, (pkg) => {
                          return (_openBlock(), _createBlock(_component_el_option, {
                            key: pkg.id,
                            label: pkg.name,
                            value: pkg.id
                          }, null, 8, ["label", "value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["model", "rules"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ])), [
    [_directive_loading, loading.value]
  ])
}
}

})